// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Sparklight — one night, played by whoever is here: bots, peers, or a mix. Solo and multiplayer are
// the SAME object with different flags, because the alternative is two code paths that disagree.
//
// ── WHY THE DECK CANNOT COME FROM THE SHARED SEED ────────────────────────────────────────────────
//
// The obvious design is to derive the shuffle from `RoundInfo.seed` and let each client simply
// decline to render its own hand. It is wrong, and not for the reason you would guess. The seed is
// broadcast by the rematch protocol to every peer and every spectator, so deriving the deck from it
// does not leak "your own hand" — it leaks THE ENTIRE FUTURE ORDER OF THE DRAW PILE to everybody.
// In a game about spending a scarce channel to tell someone what they are holding, knowing the next
// eight cards is a far larger cheat than knowing your own five, and it collapses the endgame
// completely. So: ONE peer shuffles a private order and never sends it.
//
// ── THE PART THAT CANNOT BE FIXED, STATED PLAINLY ────────────────────────────────────────────────
//
// Whoever holds the pile can work out their own hand: it is the deck minus everything revealed minus
// every other hand, all of which they know. There is no serverless arrangement that avoids this
// without mental-poker cryptography, because dealing the next card requires knowing what is left,
// and knowing what is left is knowing your own hand by subtraction. So the game says so out loud —
// `deckHolder` is surfaced in the UI and named in the results — rather than pretending otherwise.
//
// ── WHAT TRAVELS ─────────────────────────────────────────────────────────────────────────────────
//
// The whole log, every time, in every message (~45 small ints). A dropped or duplicated broadcast
// becomes a non-event, a peer that joins late is caught up by the next heartbeat, and there is no
// delta to get out of order. Alongside it, privately and per-peer, the `seen` table with that peer's
// own cards masked out — which is the only difference between any two clients' state.
//
// ── AND THE ONE THING EVERY PEER CHECKS ──────────────────────────────────────────────────────────
//
// A hint's touched-slot mask is inside the action. Every peer replays it through `apply`, and
// `legal()` re-derives the mask from its OWN sight whenever it can see the target's hand — which is
// every seat but its own. So a wrong or dishonest mask is caught independently by everyone except
// the one player it was aimed at. Tacit could not do this; here it is free.

import { makeRng, newSeed, shuffle } from '@ben-gy/game-engine/rng';
import { HIDDEN, buildDeck } from './cards';
import { decide, tierOf, viewFor, type Tier } from './bot';
import { suitsOf, type Mode } from './modes';
import {
  actSeat,
  apply,
  concede,
  createGame,
  defaultSetup,
  isTouched,
  legal,
  replay,
  type Ev,
  type Game,
} from './game';

/** A log longer than this is malformed, not slow. Prism at five players tops out around 45. */
export const MAX_LOG = 128;
/** How often the dealer re-sends everything, so a peer that missed a message catches up. */
export const BEAT_MS = 2000;
/** No word at all from a seat for this long and the crew covers it. */
export const SILENT_MS = 12000;
/** Present but not acting. The crew takes ONE turn for them, then hands the seat straight back. */
export const STALL_MS = 45000;
/** How long a promoted dealer waits for somebody to tell it what it is holding. */
export const CLAIM_MS = 3500;

export type Wire =
  /** Dealer -> ONE peer. The only message that differs per recipient, and the only private one. */
  | { t: 'sync'; r: number; e: number; seat: number; seen: number[]; log: number[]; away: number[] }
  /** Peer -> dealer. A proposed action; the dealer is the only one that decides. */
  | { t: 'act'; r: number; a: number }
  /** Peer -> dealer. "I have nothing, or my replay failed — send it all again." */
  | { t: 'need'; r: number }
  /** Anyone -> everyone. "I am still here." Silence, not a callback, is what arms the cover. */
  | { t: 'beat'; r: number }
  /** New dealer -> everyone. "I have picked up the deck; what am I holding?" */
  | { t: 'claim'; r: number; e: number }
  /** Peer -> new dealer. The cards it can see in the new dealer's hand, plus its copy of the log. */
  | { t: 'tell'; r: number; e: number; cards: number[]; log: number[] };

export interface SessionDeps {
  send: (m: Wire, to?: string | string[]) => void;
  onChange: () => void;
  onEvents: (evs: Ev[]) => void;
  onEnd: () => void;
  now?: () => number;
}

export interface SessionInit {
  mode: Mode;
  round: number;
  players: number;
  /** Which seat the local player occupies, or null for a spectator. */
  mySeat: number | null;
  /** Seats driven by the local bot. Solo fills this with every seat but yours. */
  bots: Set<number>;
  tier: Tier;
  isHost: boolean;
  names: string[];
  /** Peer id per seat, for unicasting a view. Empty in solo. */
  peerOfSeat: string[];
  seed: number;
  deps: SessionDeps;
  /** Off in tests that drive the crew by hand. */
  botDelayMs?: number;
}

export class Session {
  readonly mode: Mode;
  readonly round: number;
  readonly players: number;
  readonly tier: Tier;
  mySeat: number | null;
  bots: Set<number>;
  isHost: boolean;
  names: string[];
  peerOfSeat: string[];
  deps: SessionDeps;
  g: Game | null = null;
  /** True while a promoted dealer is waiting to be told what it is holding. */
  rebuilding = false;
  /** Seats nobody is behind any more. The crew's own bot covers them. */
  readonly away = new Set<number>();
  /** Which seat, if any, is holding the deck — surfaced in the UI, never hidden. */
  dealerSeat: number | null = null;
  epoch = 0;
  /** Set when a peer's replay disagreed with the dealer: a wrong build, not a wrong move. */
  mismatch = false;

  /** THE SECRET. Host only; never sent, never logged, never rendered. */
  private order: number[] = [];
  private readonly seed: number;
  private readonly botDelayMs: number;
  private lastEventCount = 0;
  private botTimer: ReturnType<typeof setTimeout> | null = null;
  private claimAt = 0;
  private told: number[][] = [];
  private toldLogs: number[][] = [];
  private needTries = 0;
  private ended = false;
  private heardAt = new Map<number, number>();
  /**
   * When this client started listening. A seat we have not heard from YET has been silent for
   * `now - startedAt`, NOT for `now - 0` — measuring from the epoch makes every unheard seat look
   * like it has been gone since 1970, so the first `tick()` writes off crewmates who are sitting
   * right there and the crew starts raising their cards before they have had a turn. One dropped
   * opening `need` is all it takes.
   */
  private startedAt = 0;
  private turnSince = 0;
  private covering: number | null = null;

  constructor(init: SessionInit) {
    this.mode = init.mode;
    this.round = init.round;
    this.players = init.players;
    this.tier = init.tier;
    this.mySeat = init.mySeat;
    this.bots = init.bots;
    this.isHost = init.isHost;
    this.names = init.names;
    this.peerOfSeat = init.peerOfSeat;
    this.deps = init.deps;
    this.seed = init.seed;
    this.botDelayMs = init.botDelayMs ?? 700;
    this.startedAt = this.now();

    if (this.isHost) this.dealAsHost();
    else this.deps.send({ t: 'need', r: this.round });
  }

  private now(): number {
    return this.deps.now ? this.deps.now() : Date.now();
  }

  // ── setup ──────────────────────────────────────────────────────────────────────────────────────

  private dealAsHost(): void {
    /**
     * The order is shuffled from a seed MINTED HERE and never sent, never logged, never mixed with
     * anything a peer has been given. Deriving it from the round seed — even folded through a
     * constant — would not be private at all: the rematch protocol broadcasts that seed to every peer
     * and every spectator, and the constant is in the source, so every client in the room could
     * compute the entire future order of the draw pile. See the note at the top of this file.
     */
    this.order = shuffle(makeRng(newSeed()), buildDeck(suitsOf(this.mode)));
    this.g = createGame(defaultSetup(this.mode, this.players, this.seed), this.order);
    this.dealerSeat = this.mySeat;
    this.turnSince = this.now();
    this.pushViews();
    this.changed();
    this.maybeBot();
  }

  /**
   * Send every peer the table as it is allowed to see it. One message each, because each one is
   * different — that difference IS the game.
   */
  private pushViews(to?: number): void {
    const g = this.g;
    if (!g || !this.isHost) return;
    for (let seat = 0; seat < this.players; seat++) {
      if (to !== undefined && seat !== to) continue;
      const peer = this.peerOfSeat[seat];
      if (!peer || seat === this.mySeat) continue;
      this.deps.send(
        {
          t: 'sync',
          r: this.round,
          e: this.epoch,
          seat,
          seen: maskFor(g, seat),
          log: [...g.log],
          away: [...this.away],
        },
        peer,
      );
    }
    // A spectator gets a view with nothing masked — it holds no hand to protect, and a dead screen
    // for somebody who joined a second late is the failure this exists to avoid.
    const seated = new Set(this.peerOfSeat.filter(Boolean));
    void seated;
  }

  // ── the wire ───────────────────────────────────────────────────────────────────────────────────

  receive(msg: Wire, from: string): void {
    if (msg.r !== this.round) return;

    if (msg.t === 'sync') {
      if (this.isHost) return;
      if (msg.e < this.epoch) return; // a stale broadcast from a dealer that has been replaced
      if (msg.log.length > MAX_LOG) return;
      if (msg.e === this.epoch && this.g && msg.log.length < this.g.log.length) return;
      this.adopt(msg);
      return;
    }

    if (msg.t === 'act') {
      if (!this.isHost || !this.g) return;
      /**
       * A peer may only act for ITS OWN seat. Without this one compare, any crewmate could play
       * everybody's cards — and in a game whose entire content is that you cannot see your own hand,
       * somebody else raising it for you is not a cheat so much as a different, much worse game.
       */
      const seat = actSeat(msg.a);
      if (this.peerOfSeat[seat] !== from) return;
      this.heardAt.set(seat, this.now());
      // They are demonstrably still here, so hand the seat back if the crew had picked it up.
      this.away.delete(seat);
      this.commit(msg.a);
      return;
    }

    if (msg.t === 'need') {
      if (!this.isHost || !this.g) return;
      const seat = this.peerOfSeat.indexOf(from);
      this.heardAt.set(seat, this.now());
      if (seat >= 0) this.away.delete(seat);
      this.pushViews(seat >= 0 ? seat : undefined);
      return;
    }

    if (msg.t === 'beat') {
      const seat = this.peerOfSeat.indexOf(from);
      if (seat >= 0) {
        this.heardAt.set(seat, this.now());
        if (this.isHost && this.away.delete(seat)) this.changed();
      }
      return;
    }

    if (msg.t === 'claim') {
      // Somebody else picked up the deck. Tell them what we can see of their hand, and what we think
      // the log is — the longest clean log wins, which recovers a ply the old dealer never sent.
      if (this.isHost || !this.g) return;
      const seat = this.peerOfSeat.indexOf(from);
      if (seat < 0) return;
      this.epoch = Math.max(this.epoch, msg.e);
      const cards = this.g.hands[seat].map((p) => this.g!.seen[p] ?? HIDDEN);
      if (cards.some((c) => c === HIDDEN)) return; // we cannot see it either; stay quiet
      this.deps.send({ t: 'tell', r: this.round, e: msg.e, cards, log: [...this.g.log] }, from);
      return;
    }

    if (msg.t === 'tell') {
      if (!this.isHost || !this.rebuilding || msg.e !== this.epoch) return;
      this.told.push([...msg.cards]);
      this.toldLogs.push([...msg.log]);
      this.tryRebuild();
    }
  }

  /** Replace our whole state with the dealer's. Never a delta, so a lost message costs nothing. */
  private adopt(msg: Extract<Wire, { t: 'sync' }>): void {
    const setup = defaultSetup(this.mode, this.players, this.seed);
    const rebuilt = replay(setup, msg.seen, deckSizeOf(this.mode), msg.log);
    if (!rebuilt) {
      /**
       * Our sight and the dealer's history do not fit together. Ask once or twice — a message may
       * simply have arrived out of order — and then stop, because an unbounded ask is a `need`/`sync`
       * storm that runs for the rest of the night. After that, say so: a peer showing a board that is
       * quietly several plies wrong is worse than a peer that admits it is lost.
       */
      if (++this.needTries <= 3) this.deps.send({ t: 'need', r: this.round });
      else this.mismatch = true;
      this.changed();
      return;
    }
    this.needTries = 0;
    this.mismatch = false;
    this.epoch = msg.e;
    this.mySeat = msg.seat;
    this.away.clear();
    for (const s of msg.away) this.away.add(s);
    this.g = rebuilt;
    this.changed();
  }

  // ── actions ────────────────────────────────────────────────────────────────────────────────────

  /**
   * A local action from the human. Applied by the dealer, proposed by anyone else.
   *
   * A guest MUST NOT apply optimistically, and the reason is specific to this game rather than
   * general caution: raising a card you cannot see is a branch on a hidden value. The dealer would
   * compute a plume growing and the guest would compute a failure, because `raisable(HIDDEN)` is
   * false — and from that ply on the two of them are playing different games. Proposing costs one
   * round trip and cannot desync.
   */
  submit(action: number): boolean {
    if (!this.g || this.rebuilding || this.mismatch) return false;
    if (this.isHost) return this.commit(action);
    if (actSeat(action) !== this.mySeat) return false;
    this.deps.send({ t: 'act', r: this.round, a: action });
    return true;
  }

  private commit(action: number): boolean {
    const g = this.g;
    if (!g || !this.isHost) return false;
    if (!apply(g, action, this.order)) return false;
    this.turnSince = this.now();
    this.covering = null;
    this.pushViews();
    this.changed();
    this.maybeBot();
    return true;
  }

  /** Whether the local client is responsible for playing this seat. */
  private drives(seat: number): boolean {
    if (!this.isHost) return false;
    return this.bots.has(seat) || this.away.has(seat) || this.covering === seat;
  }

  private maybeBot(): void {
    const g = this.g;
    if (!g || this.botTimer !== null || this.rebuilding || g.over) return;
    if (!this.drives(g.turn)) return;

    // setTimeout, never rAF: a backgrounded tab never fires rAF, and a crew that stops playing the
    // moment the player checks a message is a hang, not a pause.
    this.botTimer = setTimeout(() => {
      this.botTimer = null;
      const cur = this.g;
      if (!cur || cur.over || this.rebuilding || !this.drives(cur.turn)) return;
      const seat = cur.turn;
      let action: number;
      try {
        action = decide(viewFor(cur, seat), seat, this.tier).action;
      } catch {
        // viewFor throws when this client cannot see enough to drive a seat honestly. Refusing to
        // move is right: a plausible guess would be a different action from the one the real dealer
        // would have made, and nobody would ever know.
        return;
      }
      if (!legal(cur, action)) return;
      this.commit(action);
    }, this.botDelayMs);
  }

  // ── the room changing shape ────────────────────────────────────────────────────────────────────

  /**
   * A crewmate left. In a co-op there is nobody to hand a win to, so the night continues with their
   * seat played by the crew's own bot — a night that gets harder, never a board that stops.
   */
  peerLeft(peerId: string): void {
    const seat = this.peerOfSeat.indexOf(peerId);
    if (seat < 0) return;
    this.away.add(seat);
    this.peerOfSeat[seat] = '';
    this.changed();
    this.maybeWalkover();
    this.maybeBot();
  }

  /** Everyone else has gone. There is nothing left to co-operate with. */
  alone(): boolean {
    return this.mySeat !== null && this.away.size >= this.players - 1 && this.bots.size === 0;
  }

  /**
   * THE WALKOVER, and it is not a nicety — without it a two-player room HANGS.
   *
   * When the dealer leaves a two-player room, the survivor is promoted and asks the room what it is
   * holding. There is nobody left to answer. So its own hand stays hidden, which means `viewFor`
   * correctly refuses to build a view for the seat the crew's bot is supposed to be covering — and
   * the night stops dead on a board that looks perfectly fine, where it is simply nobody's turn.
   * Found by closing the host's tab on the live site and watching the survivor try ninety times to
   * take a turn, which is exactly the check the multiplayer gate exists for.
   *
   * A co-op has nobody to award the win to, so the honest ending is: stop here, keep the score, and
   * show the summary. Every peer reaches the results, which is the rule that outranks the rest.
   */
  private maybeWalkover(): void {
    if (!this.g || this.g.over || this.rebuilding) return;
    if (!this.alone()) return;
    concede(this.g, 'alone');
    this.changed();
  }

  /**
   * This peer has just been promoted, and the pile left with the last dealer.
   *
   * TWO PROBLEMS, AND ONLY ONE OF THEM IS HARD. Its own hand it genuinely does not know, so it asks —
   * and every other peer can answer, because every other peer can see it. (Tacit could only ask the
   * card's owner, who was the one person unable to be contradicted; here a lie is visible to
   * everybody.) The remaining pile it does not need to recover AT ALL: nobody has ever seen a pile
   * card, so a fresh shuffle of whatever is unaccounted for is indistinguishable from the original
   * order. There is no history to contradict, which deletes the entire class of bug that made
   * tacit's takeover delicate.
   */
  becomeHost(): void {
    if (this.isHost || !this.g || this.g.over) {
      this.isHost = true;
      this.dealerSeat = this.mySeat;
      return;
    }
    this.isHost = true;
    this.epoch++;
    this.dealerSeat = this.mySeat;
    this.rebuilding = true;
    this.claimAt = this.now();
    this.told = [];
    this.toldLogs = [];
    this.deps.send({ t: 'claim', r: this.round, e: this.epoch });
    this.changed();
    this.tryRebuild();
  }

  private tryRebuild(): void {
    const g = this.g;
    if (!g || !this.rebuilding) return;
    const anyoneToAsk = this.peerOfSeat.some((p, s) => p !== '' && s !== this.mySeat);
    if (this.told.length === 0 && anyoneToAsk && this.now() - this.claimAt < CLAIM_MS) return;

    // The longest log that replays cleanly wins. That recovers the ply the old dealer committed and
    // died before broadcasting, which is otherwise a silent one-move rollback.
    let log = [...g.log];
    for (const cand of this.toldLogs) {
      if (cand.length > log.length && cand.length <= MAX_LOG) log = [...cand];
    }

    const mine = this.told[0];
    const seen = g.seen.slice();
    if (mine && this.mySeat !== null) {
      const slots = g.hands[this.mySeat];
      for (let i = 0; i < slots.length && i < mine.length; i++) seen[slots[i]] = mine[i];
    }

    // Everything nobody can account for is the pile. Its order was never known to anyone, so this
    // shuffle is not a reconstruction — it is as good as the original.
    const rest = countDown(buildDeck(suitsOf(this.mode)), seen);
    const order = seen.slice();
    // Minted locally for the same reason as the opening shuffle: a pile derived from the round seed
    // and the epoch is a pile every peer in the room can compute.
    const pile = shuffle(makeRng(newSeed()), rest);
    for (let i = 0, p = g.drawn; p < deckSizeOf(this.mode); p++, i++) order[p] = pile[i] ?? HIDDEN;

    const setup = defaultSetup(this.mode, this.players, this.seed);
    const rebuilt = replay(setup, order, deckSizeOf(this.mode), log);
    if (rebuilt) {
      this.g = rebuilt;
      this.order = order;
    } else {
      // Keep the state we already had rather than replacing it with nothing.
      this.order = order;
    }

    /**
     * ANSWERING IS THE LIVENESS SIGNAL. A seat whose peer did not answer the claim is a seat with
     * nobody behind it — starting with the dealer that just walked out — so the crew's bot covers
     * it. Without this the night stalls in the quietest way there is: the board is correct, nothing
     * errors, and it is simply nobody's turn, for ever.
     */
    for (let s = 0; s < this.players; s++) {
      if (s === this.mySeat) continue;
      if (this.peerOfSeat[s] === '') this.away.add(s);
    }
    this.rebuilding = false;
    this.turnSince = this.now();
    this.pushViews();
    this.changed();
    this.maybeWalkover();
    this.maybeBot();
  }

  /**
   * Called on a timer by the screen. Three separate jobs that must not be collapsed into one
   * timeout: finish a rebuild nobody answered, cover a seat that has gone SILENT, and take a single
   * turn for one that is merely SLOW. A crewmate who answered the door has not left the game.
   */
  tick(): void {
    if (this.rebuilding) this.tryRebuild();
    this.maybeWalkover();
    if (!this.isHost || !this.g || this.g.over) return;
    const t = this.now();

    for (let s = 0; s < this.players; s++) {
      if (s === this.mySeat || this.bots.has(s) || this.away.has(s)) continue;
      if (!this.peerOfSeat[s]) continue;
      // Never heard from: the window runs from whichever is later — when we started listening, or
      // when we picked the deck up and asked the room to speak.
      const last = this.heardAt.get(s) ?? Math.max(this.startedAt, this.claimAt);
      if (t - last > SILENT_MS) {
        this.away.add(s);
        this.changed();
      }
    }

    // Present, but not moving. Take exactly one turn for them and hand the seat straight back.
    const seat = this.g.turn;
    if (!this.away.has(seat) && !this.bots.has(seat) && seat !== this.mySeat && this.peerOfSeat[seat]) {
      if (t - this.turnSince > STALL_MS) this.covering = seat;
    }
    this.maybeBot();
  }

  /** How long the seat to act has been thinking, so the UI can show a countdown before it covers. */
  stalledMs(): number {
    return this.now() - this.turnSince;
  }

  /** Re-broadcast everything on a heartbeat, so a peer that missed a message catches up. */
  beat(): void {
    if (this.isHost) this.pushViews();
    else this.deps.send({ t: 'beat', r: this.round });
  }

  private changed(): void {
    const g = this.g;
    if (g && g.events.length > this.lastEventCount) {
      this.deps.onEvents(g.events.slice(this.lastEventCount));
      this.lastEventCount = g.events.length;
    }
    this.deps.onChange();
    if (g && g.over && !this.ended) {
      this.ended = true;
      this.deps.onEnd();
    }
  }

  destroy(): void {
    if (this.botTimer !== null) clearTimeout(this.botTimer);
    this.botTimer = null;
  }
}

// ── helpers ───────────────────────────────────────────────────────────────────────────────────────

/** The sight table as `seat` is allowed to hold it: everything, except what it is holding. */
export function maskFor(g: Game, seat: number): number[] {
  const seen = g.seen.slice();
  for (const p of g.hands[seat]) seen[p] = HIDDEN;
  return seen;
}

export const deckSizeOf = (mode: Mode): number => buildDeck(suitsOf(mode)).length;

/** The deck minus every card anybody can account for — i.e. whatever is still in the pile. */
export function countDown(deck: number[], seen: number[]): number[] {
  const left = [...deck];
  for (const c of seen) {
    if (c === HIDDEN) continue;
    const i = left.indexOf(c);
    if (i >= 0) left.splice(i, 1);
  }
  return left;
}

export { isTouched, tierOf };
